/**
 * Les parties de la fiche de l'enfant, et où chaque manque se corrige.
 *
 * Séparé des composants pour une raison prosaïque : un fichier qui exporte à la
 * fois des composants et des constantes casse le rechargement à chaud de Vite.
 *
 * ## Les étapes du formulaire, partout
 *
 * La fiche de consultation avait ses sept sections à elle (« Informations
 * personnelles », « Allergies », « Départs »…), découpées comme les tables de
 * la base, quand la création et la vérification en ont cinq, découpées comme
 * le parent les vit. Il fallait une table de correspondance manque par manque,
 * et le parent qui cliquait « Santé » ne retrouvait pas l'écran qu'il avait
 * rempli. Depuis le 23/10/2026, la fiche se modifie avec les écrans mêmes de
 * la création : un manque se corrige dans l'étape qui l'a produit — c'est
 * `Manque.etape`, posé par la règle elle-même.
 */

import type { CompletudeEnfant, Manque } from '../../../lib/completude';
import { ETAPES, type Etape } from './fiche';

/** Les étapes qui portent des informations : toutes sauf le récapitulatif. */
export type EtapeFiche = Exclude<Etape, 'recapitulatif'>;

/** L'ordre de la page, celui du formulaire. */
export const ORDRE_ETAPES: EtapeFiche[] = ETAPES.map((e) => e.cle).filter(
  (c): c is EtapeFiche => c !== 'recapitulatif',
);

export const TITRE_ETAPE: Record<EtapeFiche, string> = {
  identite: 'Votre enfant',
  sante: 'Santé et alimentation',
  vie: 'Sa journée en stage',
  sorties: 'Qui vient le ou la chercher',
  autorisations: 'Autorisations',
};

const EST_ETAPE = new Set<string>(ORDRE_ETAPES);

/**
 * L'étape où corriger un manque : celle que la règle a posée, sinon le
 * préfixe de sa clé (« sante.medecinNom »), et l'identité en dernier recours.
 */
export function etapeDuManque(m: Manque): EtapeFiche {
  if (m.etape && EST_ETAPE.has(m.etape)) return m.etape as EtapeFiche;
  const prefixe = m.cle.split('.')[0];
  return EST_ETAPE.has(prefixe) ? (prefixe as EtapeFiche) : 'identite';
}

/** Le nombre de manques bloquants par étape. */
export function manquesParEtape(c: CompletudeEnfant): Partial<Record<EtapeFiche, number>> {
  const parEtape: Partial<Record<EtapeFiche, number>> = {};
  for (const m of c.bloquants) {
    const e = etapeDuManque(m);
    parEtape[e] = (parEtape[e] ?? 0) + 1;
  }
  return parEtape;
}
